import type { PaymentMethodViewDto, StripeSessionDto } from '@subscriptions/application/dtos';
import type { IPaymentMethodApiPort } from '@subscriptions/application/ports';
import type { PaymentSessionMode } from '@subscriptions/domain/types/SubscriptionsConst';
import type { PaymentMethodTypeType } from '@subscriptions/domain/types/SubscriptionsTypeSafe';
import type { PaymentMethodHttpClientType } from '@subscriptions/infrastructure/api/PaymentMethodHttpClient';
import type { PaymentMethodApiMapperType } from '@subscriptions/infrastructure/mappers/PaymentMethodApiMapper';

/**
 * Creates the payment method repository implementing IPaymentMethodApiPort.
 * Orchestrates HTTP client and mapper to fulfill application contracts.
 */
export const createPaymentMethodApiRepository = (props: {
  httpClient: PaymentMethodHttpClientType;
  mapper: PaymentMethodApiMapperType;
}): IPaymentMethodApiPort => {
  const { httpClient, mapper } = props;

  const listPaymentMethods = async (type: PaymentMethodTypeType): Promise<PaymentMethodViewDto[]> => {
    const apiResponse = await httpClient.listPaymentMethods(type);
    return apiResponse.map(mapper.mapPaymentMethodApiResponseToViewDto);
  };

  const generateSession = async (
    blockUuid: string,
    mode: PaymentSessionMode,
    isUpdate: boolean,
  ): Promise<StripeSessionDto> => {
    const apiResponse = await httpClient.generateSession(blockUuid, mode, isUpdate);
    return mapper.mapGenerateSessionResponseToDto(apiResponse);
  };

  const confirmSession = async (
    blockUuid: string,
    sessionId: string,
    mode: PaymentSessionMode,
    isUpdate: boolean,
  ): Promise<PaymentMethodViewDto> => {
    const apiResponse = await httpClient.confirmSession(blockUuid, sessionId, mode, isUpdate);
    return mapper.mapConfirmSessionResponseToViewDto(apiResponse);
  };

  const forcePayment = async (blockUuid: string): Promise<void> => {
    await httpClient.forcePayment(blockUuid);
  };

  return { listPaymentMethods, generateSession, confirmSession, forcePayment };
};
